/**
 * MarkdownRenderer — W66.X17
 *
 * Lightweight custom markdown-to-React renderer.
 * Handles: h1-h3, p, ul/ol/li, blockquote, em, strong, hr, code, pre, table.
 *
 * No third-party markdown library required.
 *
 * Canon guardrails (CANON_VISUALS_LOCKED.md 2026-05-29):
 *   5-color health palette: gray #6B7280, red #DC2626, orange #EA580C, yellow #EAB308, green #16A34A
 *   No exclamation points. No emojis. M-PACT hyphenated.
 *   Bryan K. Short, Founder and CEO, Mi6 Group.
 */

import React from "react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Parse inline markup: **bold**, *italic*, `code`, and plain text. */
function parseInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Tokens: **bold**, *italic*, `code`
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+?)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2] !== undefined) {
      parts.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={match.index}>{match[3]}</em>);
    } else if (match[4] !== undefined) {
      parts.push(
        <code
          key={match.index}
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.88em",
            background: "#F3F4F6",
            color: "#1F2937",
            padding: "1px 5px",
            borderRadius: 3,
          }}
        >
          {match[4]}
        </code>
      );
    }
    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : [text];
}

// ─── Token types ──────────────────────────────────────────────────────────────

type Token =
  | { type: "h1"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "hr" }
  | { type: "blockquote"; lines: string[] }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "code-block"; lang: string; lines: string[] }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "p"; text: string };

function tokenize(markdown: string): Token[] {
  const rawLines = markdown.split("\n");
  const tokens: Token[] = [];
  let i = 0;

  while (i < rawLines.length) {
    const line = rawLines[i];

    // Blank line — skip
    if (line.trim() === "") { i++; continue; }

    // Headings
    if (line.startsWith("### ")) { tokens.push({ type: "h3", text: line.slice(4).trim() }); i++; continue; }
    if (line.startsWith("## "))  { tokens.push({ type: "h2", text: line.slice(3).trim() }); i++; continue; }
    if (line.startsWith("# "))   { tokens.push({ type: "h1", text: line.slice(2).trim() }); i++; continue; }

    // HR
    if (/^---+$/.test(line.trim())) { tokens.push({ type: "hr" }); i++; continue; }

    // Code block
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < rawLines.length && !rawLines[i].startsWith("```")) {
        codeLines.push(rawLines[i]);
        i++;
      }
      i++; // consume closing ```
      tokens.push({ type: "code-block", lang, lines: codeLines });
      continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      const bqLines: string[] = [];
      while (i < rawLines.length && rawLines[i].startsWith("> ")) {
        bqLines.push(rawLines[i].slice(2));
        i++;
      }
      tokens.push({ type: "blockquote", lines: bqLines });
      continue;
    }

    // Unordered list
    if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (i < rawLines.length && /^[-*] /.test(rawLines[i])) {
        items.push(rawLines[i].slice(2).trim());
        i++;
      }
      tokens.push({ type: "ul", items });
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < rawLines.length && /^\d+\. /.test(rawLines[i])) {
        items.push(rawLines[i].replace(/^\d+\. /, "").trim());
        i++;
      }
      tokens.push({ type: "ol", items });
      continue;
    }

    // Table (GFM) — first line has pipes
    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < rawLines.length && rawLines[i].includes("|") && rawLines[i].trim().startsWith("|")) {
        tableLines.push(rawLines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        const parseRow = (row: string) =>
          row.split("|").slice(1, -1).map((c) => c.trim());
        const headers = parseRow(tableLines[0]);
        const rows = tableLines.slice(2).map(parseRow);
        tokens.push({ type: "table", headers, rows });
        continue;
      }
    }

    // Paragraph — accumulate until blank line
    const pLines: string[] = [];
    while (i < rawLines.length && rawLines[i].trim() !== "" && !rawLines[i].startsWith("#") && !rawLines[i].startsWith(">") && !/^[-*] /.test(rawLines[i]) && !/^\d+\. /.test(rawLines[i]) && !rawLines[i].startsWith("```") && !/^---+$/.test(rawLines[i].trim())) {
      pLines.push(rawLines[i]);
      i++;
    }
    if (pLines.length) {
      tokens.push({ type: "p", text: pLines.join(" ") });
    }
  }

  return tokens;
}

// ─── Render ──────────────────────────────────────────────────────────────────

interface Props {
  markdown: string;
  /** Optional max width for the content column (default 680). */
  maxWidth?: number;
  /** Override base font size (default 14). */
  fontSize?: number;
}

export default function MarkdownRenderer({ markdown, maxWidth = 680, fontSize = 14 }: Props) {
  const tokens = tokenize(markdown);

  return (
    <div
      style={{
        maxWidth,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        fontSize,
        lineHeight: 1.7,
        color: "#1F2937",
      }}
    >
      {tokens.map((token, idx) => {
        switch (token.type) {
          case "h1":
            return (
              <h1
                key={idx}
                style={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: 26,
                  fontWeight: 700,
                  color: "#0B0B0F",
                  letterSpacing: "-0.02em",
                  margin: "0 0 16px",
                  lineHeight: 1.2,
                }}
              >
                {parseInline(token.text)}
              </h1>
            );

          case "h2":
            return (
              <h2
                key={idx}
                style={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0B0B0F",
                  letterSpacing: "-0.01em",
                  margin: "32px 0 10px",
                  lineHeight: 1.3,
                }}
              >
                {parseInline(token.text)}
              </h2>
            );

          case "h3":
            return (
              <h3
                key={idx}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0B0B0F",
                  letterSpacing: "-0.01em",
                  margin: "24px 0 6px",
                  lineHeight: 1.4,
                  textTransform: "uppercase",
                }}
              >
                {parseInline(token.text)}
              </h3>
            );

          case "hr":
            return (
              <hr
                key={idx}
                style={{
                  border: "none",
                  borderTop: "1px solid #E5E7EB",
                  margin: "28px 0",
                }}
              />
            );

          case "blockquote":
            return (
              <blockquote
                key={idx}
                style={{
                  margin: "20px 0",
                  padding: "12px 20px",
                  borderLeft: "3px solid #DC2626",
                  background: "#FEF2F2",
                  color: "#1F2937",
                  fontStyle: "italic",
                  fontSize: 13.5,
                  lineHeight: 1.65,
                }}
              >
                {token.lines.map((line, li) => (
                  <p key={li} style={{ margin: li === 0 ? "0" : "8px 0 0" }}>
                    {parseInline(line)}
                  </p>
                ))}
              </blockquote>
            );

          case "ul":
            return (
              <ul
                key={idx}
                style={{ margin: "12px 0", paddingLeft: 20, listStyleType: "disc" }}
              >
                {token.items.map((item, ii) => (
                  <li
                    key={ii}
                    style={{ marginBottom: 6, color: "#374151", lineHeight: 1.6 }}
                  >
                    {parseInline(item)}
                  </li>
                ))}
              </ul>
            );

          case "ol":
            return (
              <ol
                key={idx}
                style={{ margin: "12px 0", paddingLeft: 20, listStyleType: "decimal" }}
              >
                {token.items.map((item, ii) => (
                  <li
                    key={ii}
                    style={{ marginBottom: 6, color: "#374151", lineHeight: 1.6 }}
                  >
                    {parseInline(item)}
                  </li>
                ))}
              </ol>
            );

          case "code-block":
            return (
              <pre
                key={idx}
                style={{
                  background: "#F3F4F6",
                  border: "1px solid #E5E7EB",
                  borderRadius: 6,
                  padding: "12px 16px",
                  margin: "16px 0",
                  overflowX: "auto",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12.5,
                  color: "#1F2937",
                  lineHeight: 1.55,
                }}
              >
                <code>{token.lines.join("\n")}</code>
              </pre>
            );

          case "table":
            return (
              <div
                key={idx}
                style={{ margin: "16px 0", overflowX: "auto" }}
              >
                <table
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    fontSize: 13,
                    minWidth: 400,
                  }}
                >
                  <thead>
                    <tr>
                      {token.headers.map((h, hi) => (
                        <th
                          key={hi}
                          style={{
                            padding: "8px 12px",
                            background: "#F9FAFB",
                            borderBottom: "2px solid #E5E7EB",
                            textAlign: "left",
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: "#4B5563",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {parseInline(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {token.rows.map((row, ri) => (
                      <tr
                        key={ri}
                        style={{
                          background: ri % 2 === 0 ? "#FFFFFF" : "#FAFAFA",
                          borderBottom: "1px solid #F3F4F6",
                        }}
                      >
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            style={{
                              padding: "7px 12px",
                              color: "#374151",
                              fontSize: 13,
                              verticalAlign: "top",
                            }}
                          >
                            {parseInline(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case "p":
          default:
            return (
              <p
                key={idx}
                style={{
                  margin: "0 0 14px",
                  color: "#374151",
                  lineHeight: 1.7,
                }}
              >
                {parseInline(token.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
